import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SystemHealthCard } from "./SystemHealthCard";
import {
  Activity, AlertTriangle, Archive, Copy, Database, Download, Gauge, HardDrive,
  History, Image as ImageIcon, ListTree, PlaySquare, Radio, Replace, Upload,
  Users, Zap,
} from "lucide-react";

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const MODE_ICON = { live: Radio, historical: History, mass_replace: Replace, archive: Archive, replay: PlaySquare };
const MODE_COLOR = { live: "var(--c-sky)", historical: "var(--c-teal)", mass_replace: "var(--c-violet)", archive: "var(--c-amber)", replay: "var(--c-emerald)" };

const compactRef = (value) => {
  const text = String(value || "");
  if (/^-?\d{8,}$/.test(text)) return `ID …${text.slice(-6)}`;
  return text;
};

const activityHealth = (job, lastAt) => {
  if (job.status === "rate_limited") return { label: "Rate limited", color: "var(--c-rose)", wash: "var(--t-rose)" };
  if (job.status === "queued") return { label: "Waiting", color: "var(--c-amber)", wash: "var(--t-amber)" };
  if (!lastAt) return { label: "Listening", color: "var(--c-sky)", wash: "var(--t-sky)" };
  const ageMinutes = Math.max(0, (Date.now() - lastAt.getTime()) / 60000);
  if (ageMinutes > 30) return { label: "Quiet 30m+", color: "var(--c-amber)", wash: "var(--t-amber)" };
  return { label: "Recent delivery", color: "var(--c-emerald)", wash: "var(--t-emerald)" };
};

const Stat = ({ label, value, sub, subTitle, icon: Icon, testid, accent, tint, to, alert, warning }) => {
  // One hue per card: the rail, the icon chip and the figure all agree.
  const ink = alert ? "var(--c-rose)" : warning ? "var(--c-amber)" : accent || "var(--tg-faint)";
  const wash = alert ? "var(--t-rose)" : warning ? "var(--t-amber)" : tint || "rgba(140,147,166,.12)";
  const body = (
    <Card
      className={`bg-[var(--tg-surface)] p-5 h-full min-h-[154px] border-l-4 transition-all duration-200 fade-up ${
        alert ? "border-[rgb(var(--c-rose-rgb)/0.50)] hover:border-[var(--c-rose)]" : warning ? "border-[rgb(var(--c-amber-rgb)/0.45)] hover:border-[var(--c-amber)]" : "border-[var(--tg-border)] hover:border-[var(--tg-border-hi)]"
      } ${to ? "hover:-translate-y-0.5 cursor-pointer" : ""}`}
      style={{ borderLeftColor: ink }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="label-caps">{label}</div>
        <span className="tint-chip h-8 w-8" style={{ background: wash, color: ink }}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div
        className="font-head font-bold text-3xl xl:text-4xl mt-3 tracking-tight tabular-nums truncate"
        style={alert ? { color: ink } : undefined}
      >
        {value}
      </div>
      <div className="text-[11px] text-[var(--tg-faint)] mt-1 min-h-4 truncate" title={subTitle}>{sub || "\u00a0"}</div>
    </Card>
  );
  return to ? <Link to={to} className="block h-full">{body}</Link> : body;
};

export default function Overview() {
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [health, setHealth] = useState(null);
  const fileRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      // /system/health is local-only (no Telegram round-trips), so it rides the
      // existing dashboard poll rather than needing its own interval.
      const [o, j, h] = await Promise.all([
        api.get("/overview"), api.get("/jobs"), api.get("/system/health"),
      ]);
      setData(o.data);
      setJobs(j.data);
      setHealth(h.data);
    } catch (e) {
      console.error("overview poll failed", e);
    }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [poll]);

  const exportBackup = async () => {
    try {
      const { data: backup } = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clonecast-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(errMsg(e, "Export failed"));
    }
  };

  const importBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const json = JSON.parse(await file.text());
      const { data: res } = await api.post("/backup/import", json);
      const c = res.imported || {};
      toast.success(
        `Restored ${c.accounts || 0} accounts · ${c.channels || 0} channels · ${c.rules || 0} rules · ${c.rule_templates || 0} templates · ${c.jobs || 0} jobs`
      );
      poll();
    } catch (err) {
      toast.error(errMsg(err, "Import failed — is this a valid CloneCast backup file?"));
    } finally {
      e.target.value = "";
    }
  };

  const active = jobs.filter((j) => ["running", "queued", "rate_limited"].includes(j.status));
  const empty = data && data.accounts_total === 0 && data.total_jobs === 0;
  const attention = data?.attention || 0;

  return (
    <div className="space-y-6 w-full max-w-[1800px]">
      <SystemHealthCard health={health} />
      {data && (
        <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-4" data-testid="db-status-banner">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Database className="h-4 w-4 shrink-0" style={{ color: empty ? "var(--c-amber)" : "var(--c-emerald)" }} />
              <div className="min-w-0">
                <div className="label-caps">Storage · Local SQLite</div>
                <div className="font-mono text-sm truncate mt-0.5" data-testid="db-name">
                  {data.storage || `${data.db_name} · ${data.db_backend || "SQLite (local)"}`}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm text-[var(--tg-muted)]" data-testid="db-counts">
                <b className="text-[var(--tg-strong)] tabular-nums">{data.accounts_total}</b> accounts{" · "}
                <b className="text-[var(--tg-strong)] tabular-nums">{data.total_jobs}</b> jobs{" · "}
                <b className="text-[var(--tg-strong)] tabular-nums">{data.channels}</b> channels
              </span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={exportBackup}
                        className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)] font-semibold" data-testid="export-backup-btn">
                  <Download className="h-3.5 w-3.5 mr-2" /> Export
                </Button>
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}
                        className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)] font-semibold"
                        data-testid="import-backup-btn">
                  <Upload className="h-3.5 w-3.5 mr-2" /> Import
                </Button>
                <input ref={fileRef} type="file" accept="application/json,.json" onChange={importBackup}
                       className="hidden" data-testid="import-backup-input" />
              </div>
            </div>
          </div>
          {empty && (
            <div className="flex items-center gap-2 text-[var(--c-amber)] text-xs mt-3 pt-3 border-t border-[var(--tg-border)]" data-testid="db-empty-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              This database has no accounts or jobs. If you had data before, restore your latest backup above.
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        <Stat label="Messages Copied" value={data?.total_copied?.toLocaleString() ?? "—"} icon={Copy} testid="stat-copied"
              accent="var(--c-sky)" tint="var(--t-sky)" />
        <Stat label="Throughput" value={data ? `${data.throughput_per_min ?? 0}` : "—"} sub="messages / min" subTitle="Calculated over the last 5 minutes" icon={Gauge} testid="stat-throughput"
              accent="var(--c-teal)" tint="var(--t-teal)" />
        <Stat label="Active Jobs" value={data?.running_jobs ?? "—"} sub={`of ${data?.total_jobs ?? "—"} total`} icon={Zap} testid="stat-running"
              accent="var(--c-emerald)" tint="var(--t-emerald)" to="/jobs" />
        <Stat label="Queued" value={data?.queued_jobs ?? "—"} sub={data?.rate_limited_jobs ? `${data.rate_limited_jobs} rate-limited` : "waiting for a worker slot"} icon={ListTree} testid="stat-queued"
              accent="var(--c-indigo)" tint="var(--t-indigo)" to="/jobs" />
        <Stat label="Attention" value={attention} sub={attention > 0 ? "failed jobs / accounts to re-verify" : "all clear"} icon={AlertTriangle} testid="stat-attention"
              warning={attention > 0} accent="var(--c-emerald)" tint="var(--t-emerald)" to="/jobs" />
        <Stat label="Accounts" value={data ? `${data.accounts_connected}/${data.accounts_total}` : "—"} sub="connected" icon={Users} testid="stat-accounts"
              accent={data && data.accounts_connected < data.accounts_total ? "var(--c-amber)" : "var(--c-indigo)"}
              tint={data && data.accounts_connected < data.accounts_total ? "var(--t-amber)" : "var(--t-indigo)"} to="/accounts" />
        <Stat label="Channels" value={data?.channels ?? "—"} icon={Radio} testid="stat-channels"
              accent="var(--c-indigo)" tint="var(--t-indigo)" to="/channels" />
        <Stat label="Vault Messages" value={data?.vault ? data.vault.messages.toLocaleString() : "—"} sub={data?.vault ? `${data.vault.channels} archived channels` : ""} icon={Archive} testid="stat-vault"
              accent="var(--c-violet)" tint="var(--t-violet)" to="/vault" />
        <Stat label="Vault Media" value={data?.vault ? data.vault.media_files.toLocaleString() : "—"} sub="deduplicated files" icon={ImageIcon} testid="stat-vault-media"
              accent="var(--c-violet)" tint="var(--t-violet)" to="/vault" />
        <Stat label="Vault Storage" value={data?.vault ? fmtBytes(data.vault.storage_bytes) : "—"} icon={HardDrive} testid="stat-vault-storage"
              accent="var(--c-violet)" tint="var(--t-violet)" to="/vault" />
      </div>

      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="label-caps">Live Activity ({active.length})</div>
          <Link to="/jobs" className="inline-flex items-center rounded-md border border-[rgb(var(--c-sky-rgb)/0.30)] bg-[rgb(var(--c-sky-rgb)/0.08)] px-3 py-1.5 text-sm text-[var(--c-sky)] hover:bg-[rgb(var(--c-sky-rgb)/0.15)] font-semibold transition-colors" data-testid="overview-all-jobs">
            All jobs →
          </Link>
        </div>
        {active.length === 0 ? (
          <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-12" data-testid="no-active-jobs">
            <div className="text-center">
              <Activity className="h-8 w-8 mx-auto text-[var(--tg-faint)]" />
              <div className="font-head font-bold text-2xl mt-4">No active copy jobs</div>
              <p className="text-[var(--tg-faint)] mt-2 text-sm">Create a job to start mirroring channels in real time.</p>
              <Link to="/jobs"
                    className="inline-block mt-5 px-5 py-2.5 rounded-md bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white text-sm font-semibold transition-colors"
                    data-testid="overview-create-job">
                Create a Copy Job
              </Link>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {active.map((j) => {
              const Icon = MODE_ICON[j.mode] || History;
              const color = MODE_COLOR[j.mode] || "var(--c-teal)";
              const lastAt = j.last_copied_at ? new Date(j.last_copied_at) : null;
              const validLastAt = lastAt && !isNaN(lastAt.getTime()) ? lastAt : null;
              const health = activityHealth(j, validLastAt);
              const fullRoute = j.mode === "archive" ? `${j.source} → Vault` : `${j.source} → ${j.destinations?.join(", ")}`;
              const compactRoute = j.mode === "archive"
                ? `${compactRef(j.source)} → Vault`
                : `${compactRef(j.source)} → ${(j.destinations || []).map(compactRef).join(", ")}`;
              return (
                <div key={j.id} className="tracing-border rounded-lg p-4" data-testid={`active-job-${j.id}`} style={{ borderColor: health.color }}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color }} />
                      <div className="font-head font-semibold text-sm truncate">{j.name}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={j.status === "running" ? "live-dot h-1.5 w-1.5 rounded-full bg-[var(--c-emerald)]" : "h-1.5 w-1.5 rounded-full bg-[var(--c-amber)]"} />
                      <span className="text-[9px] uppercase tracking-wider font-bold" style={{ color }}>
                        {j.status === "running" ? j.mode.replace("_", " ") : j.status.replace("_", " ")}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <div className="flex items-baseline gap-2">
                    <span className="font-head font-bold text-2xl tabular-nums">{(j.copied ?? 0).toLocaleString()}</span>
                    <span className="text-[var(--tg-faint)] text-xs">{j.mode === "archive" ? "captured" : "copied"}</span>
                    {j.total ? <span className="text-[var(--tg-faint)] text-xs">/ {j.total.toLocaleString()}</span> : null}
                    </div>
                    {j.mode === "live" && j.captured > 0 && (
                      <span className="text-[11px] text-[var(--c-amber)] tabular-nums">{j.captured} archived</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--tg-faint)] mt-1.5 font-mono truncate" title={fullRoute}>
                    {compactRoute}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-semibold" style={{ color: health.color, background: health.wash }}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: health.color }} />{health.label}
                    </span>
                    {validLastAt && <div className="text-xs text-[var(--tg-muted)]" title={validLastAt.toLocaleString()}>Last delivery · {validLastAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
